import React, { useEffect, useState, useRef, useCallback } from "react";
import styled from "styled-components";

// ─── Puzzle constants ──────────────────────────────────────────────────────────
const ROWS = 6;
const COLS = 6;
const NUMS = 6;  // values 1-6

// Base solved 6×6 grid.
// Constraints:
//   • Each ROW (6 cells)                → {1..6} each exactly once
//   • Each COLUMN (6 cells)             → {1..6} each exactly once
//   • Each 3-col × 2-row BOX (6 cells)  → {1..6} each exactly once
//   (6 boxes total: 2 col-bands × 3 row-bands)
const BASE_GRID = [
  [1, 2, 3, 4, 5, 6],
  [4, 5, 6, 1, 2, 3],
  [2, 3, 4, 5, 6, 1],
  [5, 6, 1, 2, 3, 4],
  [3, 4, 5, 6, 1, 2],
  [6, 1, 2, 3, 4, 5],
];

// ─── Puzzle engine ─────────────────────────────────────────────────────────────

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function generateSolvedGrid() {
  let grid = BASE_GRID.map((r) => [...r]);

  // 1. Relabel numbers (random permutation of 1-6)
  const perm = shuffle([1, 2, 3, 4, 5, 6]);
  const map  = {};
  for (let i = 0; i < NUMS; i++) map[i + 1] = perm[i];
  grid = grid.map((row) => row.map((v) => map[v]));

  // 2. Shuffle the 3 row-bands (each band = 2 consecutive rows)
  const bands = shuffle([[0, 1], [2, 3], [4, 5]]);
  // 3. Shuffle rows within each row-band
  const rowOrder = bands.flatMap(([a, b]) => shuffle([a, b]));
  grid = rowOrder.map((i) => [...grid[i]]);

  // 4. Shuffle the 2 column-blocks (each block = 3 consecutive cols)
  const colBlocks = shuffle([[0, 1, 2], [3, 4, 5]]);
  // 5. Shuffle cols within each block
  const colOrder = colBlocks.flatMap((block) => shuffle([...block]));
  grid = grid.map((row) => colOrder.map((c) => row[c]));

  return grid;
}

// Box: 3 cols × 2 rows  →  br = floor(r/2)*2,  bc = floor(c/3)*3
function canPlace(board, r, c, num) {
  // Row
  for (let cc = 0; cc < COLS; cc++) if (board[r][cc] === num) return false;
  // Column
  for (let rr = 0; rr < ROWS; rr++) if (board[rr][c] === num) return false;
  // Box
  const br = Math.floor(r / 2) * 2;
  const bc = Math.floor(c / 3) * 3;
  for (let rr = br; rr < br + 2; rr++)
    for (let cc = bc; cc < bc + 3; cc++)
      if (board[rr][cc] === num) return false;
  return true;
}

// Count solutions up to `limit` — MRV backtracking
function countSolutions(board, limit = 2) {
  let count = 0;

  function bt(b) {
    if (count >= limit) return;

    let best = null, minCnds = NUMS + 1;

    outer: for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        if (b[r][c] === 0) {
          let cnds = 0;
          for (let n = 1; n <= NUMS; n++) if (canPlace(b, r, c, n)) cnds++;
          if (cnds === 0) return;
          if (cnds < minCnds) { minCnds = cnds; best = { r, c }; if (cnds === 1) break outer; }
        }
      }
    }

    if (!best) { count++; return; }

    const { r, c } = best;
    for (let n = 1; n <= NUMS; n++) {
      if (count >= limit) return;
      if (canPlace(b, r, c, n)) { b[r][c] = n; bt(b); b[r][c] = 0; }
    }
  }

  bt(board.map((row) => [...row]));
  return count;
}

const REMOVE = { easy: 12, medium: 18, hard: 28 };

function generatePuzzle(difficulty) {
  const solution = generateSolvedGrid();
  const puzzle   = solution.map((r) => [...r]);
  const target   = REMOVE[difficulty] ?? 14;

  const positions = shuffle(
    Array.from({ length: ROWS * COLS }, (_, i) => [Math.floor(i / COLS), i % COLS])
  );

  let removed = 0;
  for (const [r, c] of positions) {
    if (removed >= target) break;
    const backup = puzzle[r][c];
    puzzle[r][c] = 0;
    if (countSolutions(puzzle) === 1) removed++;
    else puzzle[r][c] = backup;
  }
  return { puzzle, solution };
}

// ─── localStorage ─────────────────────────────────────────────────────────────

const LS_KEY = (d) => `minisudoku_scores_${d}`;

function loadAllScores() {
  const out = {};
  ["easy", "medium", "hard"].forEach((d) => {
    try { out[d] = JSON.parse(localStorage.getItem(LS_KEY(d)) || "[]"); }
    catch { out[d] = []; }
  });
  return out;
}

function persistScore(diff, entry) {
  try {
    const prev    = JSON.parse(localStorage.getItem(LS_KEY(diff)) || "[]");
    const updated = [...prev, entry]
      .sort((a, b) => a.seconds - b.seconds || a.mistakes - b.mistakes)
      .slice(0, 3);
    localStorage.setItem(LS_KEY(diff), JSON.stringify(updated));
    return updated;
  } catch { return []; }
}

const fmt = (s) =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

// ─── Styled components ─────────────────────────────────────────────────────────

const Container = styled.div`
  min-height: 100vh;
  padding: 28px 16px 80px;
  background: radial-gradient(circle at top left, rgba(79, 70, 229, 0.14), transparent 35%),
    linear-gradient(180deg, #070b20 0%, #0f183f 100%);
  color: ${({ theme }) => theme.text_primary};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Wrap = styled.div`
  width: 100%;
  max-width: 420px;
`;

const PageTitle = styled.h1`
  margin: 0 0 4px;
  font-size: clamp(26px, 7vw, 40px);
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.primary};
`;

const PageSub = styled.p`
  margin: 0 0 22px;
  font-size: 13px;
  color: ${({ theme }) => theme.text_secondary};
`;

// Setup
const SetupCard = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 20px;
  padding: 28px 22px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 8px;
`;

const NameInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 13px 16px;
  border-radius: 12px;
  border: 1.5px solid rgba(255,255,255,0.12);
  background: ${({ theme }) => theme.bg};
  color: ${({ theme }) => theme.text_primary};
  font-size: 15px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.2s;
  &:focus { border-color: ${({ theme }) => theme.primary}; }
`;

const DiffRow = styled.div`
  display: flex;
  gap: 8px;
`;

const DiffBtn = styled.button`
  flex: 1;
  padding: 10px 4px;
  border-radius: 10px;
  border: 1.5px solid ${({ $a, theme }) => ($a ? theme.primary : "rgba(255,255,255,0.12)")};
  background: ${({ $a, theme }) => ($a ? `${theme.primary}22` : "transparent")};
  color: ${({ $a, theme }) => ($a ? theme.primary : theme.text_secondary)};
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: capitalize;
`;

const PrimaryBtn = styled.button`
  width: 100%;
  padding: 15px;
  border-radius: 14px;
  border: none;
  background: ${({ theme }) => theme.primary};
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover:not(:disabled) { opacity: 0.86; }
  &:disabled { opacity: 0.35; cursor: default; }
`;

const BestLine = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.text_secondary};
  padding: 2px 0;
`;

// Stats bar
const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 7px;
  margin-bottom: 12px;
  @media (max-width: 340px) { grid-template-columns: repeat(2, 1fr); }
`;

const StatBox = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 12px;
  padding: 10px 8px;
  text-align: center;
`;

const StatLbl = styled.div`
  font-size: 9px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 3px;
`;

const StatVal = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: ${({ $red, theme }) => ($red ? "#ef4444" : theme.text_primary)};
`;

// Grid — 6×6
const GridOuter = styled.div`
  width: 100%;
  max-width: 420px;
  border: 2.5px solid rgba(255,255,255,0.3);
  border-radius: 8px;
  overflow: hidden;
`;

const GRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  border-bottom: ${({ $thick }) =>
    $thick ? "2.5px solid rgba(255,255,255,0.3)" : "1px solid rgba(255,255,255,0.08)"};
  &:last-child { border-bottom: none; }
`;

const Cell = styled.button`
  width: 100%;
  aspect-ratio: 1;
  padding: 0;
  border: none;
  border-right: ${({ $thick }) =>
    $thick ? "2.5px solid rgba(255,255,255,0.3)" : "1px solid rgba(255,255,255,0.08)"};
  &:last-child { border-right: none; }
  background: ${({ $sel, $hi, $err }) => {
    if ($err) return "rgba(239,68,68,0.22)";
    if ($sel) return "rgba(99,102,241,0.38)";
    if ($hi)  return "rgba(255,255,255,0.055)";
    return "transparent";
  }};
  color: ${({ $given, $err, theme }) =>
    $err ? "#ef4444" : $given ? theme.text_primary : theme.primary};
  font-size: clamp(16px, 5vw, 26px);
  font-weight: ${({ $given }) => ($given ? "700" : "500")};
  cursor: ${({ $given }) => ($given ? "default" : "pointer")};
  transition: background 0.12s;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NotesWrap = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
  width: 100%;
  height: 100%;
  padding: 2px;
  box-sizing: border-box;
`;

const NoteN = styled.span`
  font-size: clamp(5px, 1.6vw, 8px);
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.text_secondary};
`;

// Controls
const Controls = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 9px;
`;

const NumPad = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
`;

const NumBtn = styled.button`
  aspect-ratio: 1;
  border-radius: 12px;
  border: 1.5px solid rgba(255,255,255,0.1);
  background: ${({ $done, theme }) => ($done ? "rgba(255,255,255,0.03)" : theme.card)};
  color: ${({ $done, theme }) => ($done ? "rgba(255,255,255,0.18)" : theme.text_primary)};
  font-size: clamp(18px, 5vw, 26px);
  font-weight: 700;
  font-family: inherit;
  cursor: ${({ $done }) => ($done ? "default" : "pointer")};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.12s, border-color 0.12s;
  &:hover:not(:disabled) {
    background: ${({ $done, theme }) => (!$done ? `${theme.primary}28` : undefined)};
    border-color: ${({ $done, theme }) => (!$done ? theme.primary : undefined)};
  }
`;

const NumRemain = styled.span`
  font-size: 8px;
  font-weight: 400;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1;
`;

const ActRow = styled.div`
  display: flex;
  gap: 7px;
`;

const ActBtn = styled.button`
  flex: 1;
  padding: 11px 6px;
  border-radius: 11px;
  border: 1.5px solid ${({ $on, theme }) => ($on ? theme.primary : "rgba(255,255,255,0.1)")};
  background: ${({ $on, theme }) => ($on ? `${theme.primary}22` : theme.card)};
  color: ${({ $on, theme }) => ($on ? theme.primary : theme.text_secondary)};
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  &:hover { color: ${({ theme }) => theme.primary}; }
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
`;

// Win
const WinCard = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.primary}55;
  border-radius: 20px;
  padding: 26px 20px;
  text-align: center;
  width: 100%;
`;

const WinTitle = styled.h2`
  margin: 0 0 6px;
  font-size: clamp(22px, 7vw, 32px);
  color: ${({ theme }) => theme.primary};
`;

const WinSub = styled.p`
  margin: 0 0 18px;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 14px;
`;

const ScoreLbl = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 8px;
  text-align: left;
`;

const ScoreRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 9px 12px;
  border-radius: 10px;
  background: ${({ $hi, theme }) => ($hi ? `${theme.primary}22` : "rgba(255,255,255,0.04)")};
  margin-bottom: 5px;
  font-size: 13px;
`;

// ─── Component ─────────────────────────────────────────────────────────────────

export default function MiniSudoku() {
  const [playerName, setPlayerName] = useState("");
  const [started,    setStarted]    = useState(false);
  const [board,      setBoard]      = useState(null);
  const [solution,   setSolution]   = useState(null);
  const [given,      setGiven]      = useState(null);
  const [notes,      setNotes]      = useState(null);
  const [selected,   setSelected]   = useState(null);
  const [mistakes,   setMistakes]   = useState(0);
  const [seconds,    setSeconds]    = useState(0);
  const [completed,  setCompleted]  = useState(false);
  const [difficulty, setDifficulty] = useState("easy");
  const [notesMode,  setNotesMode]  = useState(false);
  const [highScores, setHighScores] = useState({});
  const [lastEntry,  setLastEntry]  = useState(null);

  const timerRef = useRef(null);
  const secRef   = useRef(0);
  const errRef   = useRef(0);

  useEffect(() => { secRef.current = seconds;  }, [seconds]);
  useEffect(() => { errRef.current = mistakes; }, [mistakes]);
  useEffect(() => { setHighScores(loadAllScores()); }, []);
  useEffect(() => () => clearInterval(timerRef.current), []);

  const handleStart = useCallback(() => {
    if (!playerName.trim()) return;
    const { puzzle, solution: sol } = generatePuzzle(difficulty);
    setBoard(puzzle.map((r) => [...r]));
    setSolution(sol);
    setGiven(puzzle.map((r) => r.map((v) => v !== 0)));
    setNotes(Array.from({ length: ROWS }, () => Array.from({ length: COLS }, () => [])));
    setSelected(null);
    setMistakes(0);
    setSeconds(0);
    setCompleted(false);
    setNotesMode(false);
    setLastEntry(null);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    setStarted(true);
  }, [playerName, difficulty]);

  const handleCellClick = useCallback(
    (r, c) => { if (board && !given?.[r]?.[c]) setSelected({ r, c }); },
    [board, given]
  );

  const handleInput = useCallback(
    (num) => {
      if (!selected || !board || !given || !solution) return;
      const { r, c } = selected;
      if (given[r][c]) return;

      if (notesMode && num > 0) {
        setNotes((prev) => {
          const next = prev.map((row) => row.map((a) => [...a]));
          const arr  = next[r][c];
          const idx  = arr.indexOf(num);
          if (idx === -1) arr.push(num); else arr.splice(idx, 1);
          return next;
        });
        return;
      }

      if (num === 0) {
        setBoard((prev) => { const n = prev.map((row) => [...row]); n[r][c] = 0; return n; });
        setNotes((prev) => { const n = prev.map((row) => row.map((a) => [...a])); n[r][c] = []; return n; });
        return;
      }

      const correct = solution[r][c];
      const isRight = num === correct;
      if (!isRight) setMistakes((m) => m + 1);

      const newBoard = board.map((row) => [...row]);
      newBoard[r][c] = num;
      setBoard(newBoard);

      if (isRight) {
        setNotes((prev) => {
          const next = prev.map((row) => row.map((a) => [...a]));
          next[r][c] = [];
          for (let rr = 0; rr < ROWS; rr++) next[rr][c] = next[rr][c].filter((n) => n !== num);
          for (let cc = 0; cc < COLS; cc++) next[r][cc] = next[r][cc].filter((n) => n !== num);
          const br = Math.floor(r / 2) * 2, bc = Math.floor(c / 3) * 3;
          for (let rr = br; rr < br + 2; rr++)
            for (let cc = bc; cc < bc + 3; cc++)
              next[rr][cc] = next[rr][cc].filter((n) => n !== num);
          return next;
        });
      }

      const solved = newBoard.every((row, ri) => row.every((v, ci) => v === solution[ri][ci]));
      if (solved) {
        clearInterval(timerRef.current);
        setCompleted(true);
        const entry = {
          name:     playerName,
          seconds:  secRef.current,
          mistakes: errRef.current + (isRight ? 0 : 1),
          date:     new Date().toLocaleDateString(),
        };
        setLastEntry(entry);
        const updated = persistScore(difficulty, entry);
        setHighScores((prev) => ({ ...prev, [difficulty]: updated }));
      }
    },
    [selected, board, given, solution, notesMode, playerName, difficulty]
  );

  const cellError = useCallback(
    (r, c) =>
      board && solution && given && !given[r][c] &&
      board[r][c] !== 0 && board[r][c] !== solution[r][c],
    [board, solution, given]
  );

  const isHighlighted = useCallback(
    (r, c) => {
      if (!selected) return false;
      const { r: sr, c: sc } = selected;
      if (r === sr || c === sc) return true;
      return Math.floor(r / 2) === Math.floor(sr / 2) && Math.floor(c / 3) === Math.floor(sc / 3);
    },
    [selected]
  );

  const remaining = {};
  if (board && solution) {
    for (let n = 1; n <= NUMS; n++) {
      let placed = 0, total = 0;
      for (let r = 0; r < ROWS; r++)
        for (let c = 0; c < COLS; c++) {
          if (solution[r][c] === n) total++;
          if (board[r][c] === n && solution[r][c] === n) placed++;
        }
      remaining[n] = total - placed;
    }
  }

  // ── Setup ──────────────────────────────────────────────────────────────────
  if (!started) {
    return (
      <Container>
        <Wrap>
          <PageTitle>Mini Sudoku</PageTitle>
          <PageSub>6×6 grid · 2×3 boxes · Numbers 1–6</PageSub>
          <SetupCard>
            <div>
              <FieldLabel>Your Name</FieldLabel>
              <NameInput
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Enter your name"
                onKeyDown={(e) => e.key === "Enter" && handleStart()}
              />
            </div>
            <div>
              <FieldLabel>Difficulty</FieldLabel>
              <DiffRow>
                {["easy", "medium", "hard"].map((d) => (
                  <DiffBtn key={d} $a={difficulty === d} onClick={() => setDifficulty(d)}>
                    {d.charAt(0).toUpperCase() + d.slice(1)}
                  </DiffBtn>
                ))}
              </DiffRow>
            </div>
            <div>
              <FieldLabel>Best Scores</FieldLabel>
              {["easy", "medium", "hard"].map((d) => {
                const sc = highScores[d] || [];
                if (!sc.length) return <BestLine key={d}>{d}: —</BestLine>;
                return (
                  <BestLine key={d}>
                    {d}: {fmt(sc[0].seconds)} · {sc[0].mistakes} mistake{sc[0].mistakes !== 1 ? "s" : ""}{" "}
                    <span style={{ opacity: 0.55 }}>({sc[0].name})</span>
                  </BestLine>
                );
              })}
            </div>
            <PrimaryBtn onClick={handleStart} disabled={!playerName.trim()}>
              Start Game
            </PrimaryBtn>
          </SetupCard>
        </Wrap>
      </Container>
    );
  }

  // ── Win screen ─────────────────────────────────────────────────────────────
  if (completed) {
    const scores = highScores[difficulty] || [];
    return (
      <Container>
        <Wrap>
          <WinCard>
            <WinTitle>Puzzle Solved!</WinTitle>
            <WinSub>
              {playerName} · {fmt(seconds)} · {mistakes} mistake{mistakes !== 1 ? "s" : ""} ·{" "}
              <span style={{ textTransform: "capitalize" }}>{difficulty}</span>
            </WinSub>
            <ScoreLbl>
              Top {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)} Scores
            </ScoreLbl>
            {scores.map((s, i) => {
              const isMe =
                lastEntry &&
                s.name === lastEntry.name &&
                s.seconds === lastEntry.seconds &&
                s.mistakes === lastEntry.mistakes;
              return (
                <ScoreRow key={i} $hi={isMe}>
                  <span>#{i + 1}  {s.name}</span>
                  <span>{fmt(s.seconds)} · {s.mistakes} ✗</span>
                </ScoreRow>
              );
            })}
            <PrimaryBtn style={{ marginTop: 18 }} onClick={handleStart}>
              Play Again
            </PrimaryBtn>
            <div style={{ marginTop: 10 }}>
              <BackBtn onClick={() => { setStarted(false); setCompleted(false); }}>
                ← Back to Menu
              </BackBtn>
            </div>
          </WinCard>
        </Wrap>
      </Container>
    );
  }

  // ── Game screen ────────────────────────────────────────────────────────────
  return (
    <Container>
      <Wrap>
        <TopBar>
          <BackBtn onClick={() => { clearInterval(timerRef.current); setStarted(false); }}>
            ← Menu
          </BackBtn>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.38)", textTransform: "capitalize" }}>
            {playerName} · {difficulty}
          </span>
        </TopBar>

        <StatsRow>
          <StatBox><StatLbl>Time</StatLbl><StatVal>{fmt(seconds)}</StatVal></StatBox>
          <StatBox>
            <StatLbl>Mistakes</StatLbl>
            <StatVal $red={mistakes > 0}>{mistakes}</StatVal>
          </StatBox>
          <StatBox>
            <StatLbl>Level</StatLbl>
            <StatVal style={{ fontSize: 12, textTransform: "capitalize" }}>{difficulty}</StatVal>
          </StatBox>
          <StatBox>
            <StatLbl>Best</StatLbl>
            <StatVal style={{ fontSize: 13 }}>
              {highScores[difficulty]?.[0] ? fmt(highScores[difficulty][0].seconds) : "—"}
            </StatVal>
          </StatBox>
        </StatsRow>

        <GridOuter>
          {board.map((row, r) => (
            // thick border after rows 1 and 3 (separates the 3 two-row bands)
            <GRow key={r} $thick={r === 1 || r === 3}>
              {row.map((val, c) => {
                const isSel = selected?.r === r && selected?.c === c;
                const hi    = !isSel && isHighlighted(r, c);
                const err   = cellError(r, c);
                const gvn   = given?.[r]?.[c];
                const cn    = notes?.[r]?.[c] || [];

                return (
                  <Cell
                    key={c}
                    // thick border after col 2 (separates the 2 three-col bands)
                    $thick={c === 2}
                    $sel={isSel}
                    $hi={hi}
                    $err={err}
                    $given={gvn}
                    onClick={() => handleCellClick(r, c)}
                  >
                    {val !== 0 ? (
                      val
                    ) : cn.length > 0 ? (
                      <NotesWrap>
                        {[1, 2, 3, 4, 5, 6].map((n) => (
                          <NoteN key={n}>{cn.includes(n) ? n : ""}</NoteN>
                        ))}
                      </NotesWrap>
                    ) : null}
                  </Cell>
                );
              })}
            </GRow>
          ))}
        </GridOuter>

        <Controls>
          <NumPad>
            {[1, 2, 3, 4, 5, 6].map((n) => {
              const left = remaining[n] ?? 0;
              return (
                <NumBtn key={n} $done={left === 0} onClick={() => handleInput(n)}>
                  {n}
                  <NumRemain>{left > 0 ? left : "✓"}</NumRemain>
                </NumBtn>
              );
            })}
          </NumPad>
          <ActRow>
            <ActBtn $on={notesMode} onClick={() => setNotesMode((m) => !m)}>
              ✏️ Notes {notesMode ? "ON" : "OFF"}
            </ActBtn>
            <ActBtn onClick={() => handleInput(0)}>⌫ Erase</ActBtn>
            <ActBtn onClick={handleStart}>↺ New</ActBtn>
          </ActRow>
        </Controls>
      </Wrap>
    </Container>
  );
}
